import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import StructuredData from "@/components/feature/StructuredData";
import { movies, categories } from "@/mocks/movies";

function MovieCard({ movie }: { movie: typeof movies[0] }) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 天美麻花星空影视免费在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-bold px-2 py-1 rounded-md">
          {movie.rating}
        </div>
        {movie.status && (
          <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md">
            {movie.status}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-xs text-foreground-500">{movie.year}</span>
          <span className="text-xs text-foreground-400">|</span>
          <span className="text-xs text-foreground-500">{movie.categoryName}</span>
          {movie.duration && (
            <>
              <span className="text-xs text-foreground-400">|</span>
              <span className="text-xs text-foreground-500">{movie.duration}</span>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {movie.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const heroMovies = movies.filter((m) => m.category === "drama" || m.category === "movie").slice(0, 4);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroMovies.length]);

  const currentHero = heroMovies[heroIndex];
  const hotMovies = movies.filter((m) => m.rating >= 8.5).slice(0, 8);
  const latestMovies = [...movies].sort((a, b) => b.year - a.year).slice(0, 8);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "天美麻花星空影视",
    url: "https://www.tianmeimahua.com",
    description: "天美麻花星空影视 - 超清蓝光电视剧全集、电影大全不缓冲免费在线观看，大屏TV版最新综艺动漫免费看",
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "天美麻花星空影视",
    url: "https://www.tianmeimahua.com",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.tianmeimahua.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "天美麻花星空影视 - 免费观看电视，超清蓝光电视剧全集",
    description: "天美麻花星空影视免费观看电视，超清蓝光电视剧全集、电影大全不缓冲免费在线，大屏TV版最新综艺动漫免费看，高清影视平台。",
    url: "https://www.tianmeimahua.com",
  };

  return (
    <div className="min-h-screen bg-background-50">
      <StructuredData
        organizationSchema={organizationSchema}
        websiteSchema={websiteSchema}
        webpageSchema={webpageSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        {currentHero && (
          <>
            <img
              src={currentHero.banner}
              alt={currentHero.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          </>
        )}
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-4 md:px-8">
            <div className="max-w-2xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                天美麻花星空影视
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-6 max-w-lg">
                超清蓝光电视剧全集 · 电影大全不缓冲免费在线观看 · 大屏TV版最新综艺动漫免费看
              </p>
              <div className="flex flex-wrap gap-3">
                {heroMovies.map((m, idx) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      idx === heroIndex
                        ? "bg-primary-500 text-white"
                        : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
                    }`}
                  >
                    {m.title}
                  </Link>
                ))}
              </div>
              <div className="flex gap-2 mt-6">
                {heroMovies.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setHeroIndex(idx)}
                    className={`w-8 h-1 rounded-full transition-all cursor-pointer ${
                      idx === heroIndex ? "bg-primary-500 w-12" : "bg-white/40 hover:bg-white/60"
                    }`}
                    aria-label={`切换到第${idx + 1}张`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Quick Nav */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                <i className={`${cat.icon} text-2xl text-primary-500 group-hover:text-white transition-colors`} />
              </div>
              <span className="text-sm font-heading font-semibold text-foreground-800">{cat.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hot Recommendations */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-1">评分最高的精选影视内容</p>
          </div>
          <Link
            to="/category/drama"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
          >
            查看更多 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {hotMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* Latest Updates */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              最新上线
            </h2>
            <p className="text-sm text-foreground-500 mt-1">热播影视持续更新中</p>
          </div>
          <Link
            to="/category/movie"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap flex items-center gap-1"
          >
            查看更多 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {latestMovies.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      {/* TV Version Banner */}
      <section className="w-full px-4 md:px-8 py-10 md:py-16">
        <div className="relative rounded-xl overflow-hidden bg-gradient-to-r from-background-800 to-background-900 p-8 md:p-12">
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
                大屏TV版 · 家庭影院体验
              </h3>
              <p className="text-white/70 mb-6 max-w-md leading-relaxed">
                天美麻花星空影视TV版，支持智能电视、投影仪等大屏设备。超清蓝光画质投到大屏幕上，享受影院级别的观影体验。全家一起看剧，更过瘾！
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full whitespace-nowrap">
                  超清蓝光
                </span>
                <span className="px-4 py-2 bg-white/15 text-white text-sm font-medium rounded-full whitespace-nowrap backdrop-blur-sm">
                  不缓冲
                </span>
                <span className="px-4 py-2 bg-white/15 text-white text-sm font-medium rounded-full whitespace-nowrap backdrop-blur-sm">
                  多设备支持
                </span>
              </div>
            </div>
            <div className="flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-accent-500/20 flex items-center justify-center border-2 border-accent-400/30">
                <i className="ri-tv-line text-4xl md:text-5xl text-accent-400" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
            { value: "10万+", label: "影视资源" },
            { value: "4K蓝光", label: "最高画质" },
            { value: "每日更新", label: "片源频率" },
            { value: "1000万+", label: "活跃用户" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
              <div className="text-sm text-foreground-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}